// Local video library (sources-v2 D8): one entry per file, deduped by `isSameEntry`. Tiers by `storage`: 'handle' (Recent, evicted by LRU), 'folder' (a linked folder's file; one grant on the folder), 'opfs' (an imported copy, opens with no prompt).
import { IS_DEV } from '~/lib/constants'
import { idbLibraryStorage } from '~/lib/videoFileHandle'
import { detectFps } from '~/lib/videoFps'

export const RECENT_MAX = 20
export const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.mkv', '.avi', '.m4v']
const THUMB_WIDTH = 320
const PROBE_TIMEOUT_MS = 5000
const SCAN_DEPTH = 4
const OPFS_DIR = 'videos'

export const isVideoName = name =>
	!name.startsWith('.') && VIDEO_EXTENSIONS.some(ext => name.toLowerCase().endsWith(ext))

/** Imported copies in the origin-private file system. */
export const opfsFiles = {
	async dir() {
		const root = await navigator.storage.getDirectory()
		return root.getDirectoryHandle(OPFS_DIR, { create: true })
	},
	async write(name, file) {
		const handle = await (await this.dir()).getFileHandle(name, { create: true })
		await file.stream().pipeTo(await handle.createWritable())
	},
	async read(name) {
		return (await (await this.dir()).getFileHandle(name)).getFile()
	},
	async remove(name) {
		await (await this.dir()).removeEntry(name)
	},
	persist: () => navigator.storage.persist?.() ?? Promise.resolve(false),
	persisted: () => navigator.storage.persisted?.() ?? Promise.resolve(false),
	estimate: () => navigator.storage.estimate(),
}

/** 'granted' | 'needsGesture' | 'denied'. `requestPermission` outside a gesture throws SecurityError. */
const permission = async (handle, request) => {
	let perm = await handle.queryPermission({ mode: 'read' })
	if (perm === 'prompt') {
		if (!request) return 'needsGesture'
		try {
			perm = await handle.requestPermission({ mode: 'read' })
		} catch (e) {
			if (e?.name === 'SecurityError') return 'needsGesture'
			throw e
		}
	}
	return perm === 'granted' ? 'granted' : 'denied'
}

/** Every video under `dir` as `{ path, handle }`, `path` relative to `dir`. */
async function* walk(dir, path = [], depth = 0) {
	for await (const [name, handle] of dir.entries()) {
		if (handle.kind === 'directory') {
			if (depth < SCAN_DEPTH && !name.startsWith('.')) yield* walk(handle, [...path, name], depth + 1)
		} else if (isVideoName(name)) yield { path: [...path, name], handle }
	}
}

const samePath = (a, b) => a.length === b.length && a.every((s, i) => s === b[i])

/** A folder file's id: stable per folder and path, so a folder linked again gives presets back the ids they saved. */
const folderEntryId = async (folderId, path) => {
	const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${folderId}\n${path.join('/')}`))
	return [...new Uint8Array(digest, 0, 16)].map(b => b.toString(16).padStart(2, '0')).join('')
}

/** `inner`'s path under `outer`, or null when it is not inside it (or is it). */
const pathWithin = async (outer, inner) => {
	const path = await outer.resolve(inner).catch(() => null)
	return path?.length ? path : null
}

// Unlinked folders whose ids a later link of the same directory takes back.
const RETIRED_MAX = 20

/** Duration, size and a first-frame JPEG through a throwaway `<video>`; fps from the container. Fields it cannot read stay null. */
export async function probeVideoFile(file) {
	const url = URL.createObjectURL(file)
	const video = document.createElement('video')
	video.muted = true
	video.preload = 'auto'
	try {
		const frame = await new Promise((resolve, reject) => {
			const timer = setTimeout(() => reject(new Error('probe timeout')), PROBE_TIMEOUT_MS)
			video.onloadeddata = () => {
				clearTimeout(timer)
				resolve()
			}
			video.onerror = () => {
				clearTimeout(timer)
				reject(video.error)
			}
			video.src = url
		}).then(
			() => true,
			() => false
		)
		const { videoWidth: width, videoHeight: height, duration } = video
		let thumb = null
		if (frame && width && height) {
			const canvas = document.createElement('canvas')
			canvas.width = THUMB_WIDTH
			canvas.height = Math.round((THUMB_WIDTH * height) / width)
			canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
			thumb = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8))
		}
		const { fps } = await detectFps(file)
		return {
			duration: Number.isFinite(duration) ? duration : null,
			width: width || null,
			height: height || null,
			fps,
			thumb,
		}
	} finally {
		video.removeAttribute('src')
		video.load()
		URL.revokeObjectURL(url)
	}
}

/**
 * @param {object} storage - `all()`, `put(entry)`, `delete(id)`, `getMeta(key)`, `setMeta(key, value)`, `legacy()` → `{ current, recent }` of pre-library `{ handle, fileName }` records, `clearLegacy()`
 * @param {object} [opts]
 * @param {(file: File) => Promise<object>} [opts.probe] - metadata for a newly seen or changed file
 * @param {object} [opts.files] - imported copies: `write(name, file)`, `read(name)`, `remove(name)`, `persist()`, `persisted()`, `estimate()`
 */
export function createLibrary(storage, { probe = probeVideoFile, files = opfsFiles } = {}) {
	const logger = console.scope('library')
	const listeners = new Set()
	let chain = Promise.resolve()
	// One probe at a time: a folder scan can queue hundreds of files.
	let probes = Promise.resolve()
	let ready = null
	let lastStamp = 0
	let persistAsked = false

	/** `usedAt` that strictly increases, so two uses in one millisecond still order. */
	const stamp = () => (lastStamp = Math.max(Date.now(), lastStamp + 1))

	const notify = () => {
		for (const fn of listeners) fn()
	}

	/** Mutations run one at a time, so a concurrent `add` of one file never makes two entries. */
	const serial = fn => {
		const run = chain.then(fn)
		chain = run.catch(() => {})
		return run
	}

	const newEntry = fields => ({
		id: crypto.randomUUID(),
		name: null,
		size: null,
		duration: null,
		fps: null,
		width: null,
		height: null,
		thumb: null,
		storage: 'handle',
		ref: null,
		origin: null,
		settings: null,
		usedAt: 0,
		...fields,
	})

	/** The file handle an entry came from: Recent's own; a folder file's or an import's `origin`. */
	const fileHandleOf = e => (e.storage === 'handle' ? e.ref : e.origin)

	const findByHandle = async (entries, handle) => {
		for (const e of entries) {
			const own = fileHandleOf(e)
			if (!own) continue
			try {
				if (await own.isSameEntry(handle)) return e
			} catch {
				// A handle whose file is gone compares false.
			}
		}
		return null
	}

	const folderList = async () => (await storage.getMeta('folders')) ?? []

	const updateFolder = async (id, patch) =>
		storage.setMeta(
			'folders',
			(await folderList()).map(f => (f.id === id ? { ...f, ...patch } : f))
		)

	/** Drops Recent entries past `RECENT_MAX`; returns their ids. */
	const evict = async () => {
		const recent = (await storage.all()).filter(e => e.storage === 'handle').sort((a, b) => b.usedAt - a.usedAt)
		const over = recent.slice(RECENT_MAX)
		for (const e of over) await storage.delete(e.id)
		return over.map(e => e.id)
	}

	/** Adds or touches the entry for `handle`; returns it and whether its metadata needs a probe. */
	const upsert = async (handle, { name, size = null, usedAt = stamp() }) => {
		const found = await findByHandle(await storage.all(), handle)
		if (found) {
			// An import is a copy: the picked file changing on disk does not change it.
			const changed = found.storage !== 'opfs' && size != null && found.size !== size
			const entry = { ...found, name, usedAt, ...(changed && { size, duration: null }) }
			await storage.put(entry)
			return entry
		}
		const entry = newEntry({ name, size, ref: handle, usedAt })
		await storage.put(entry)
		await evict()
		return entry
	}

	const migrate = async () => {
		const { current, recent } = await storage.legacy()
		if (!current && !recent.length) return
		// Newest first in the old list: replay it oldest first so the order survives.
		for (const { handle, fileName } of [...recent].reverse()) {
			if (handle) await upsert(handle, { name: fileName })
		}
		if (current?.handle) {
			const entry = await upsert(current.handle, { name: current.fileName })
			await storage.setMeta('current', entry.id)
		}
		await storage.clearLegacy()
		logger.info(`migrated ${recent.length} recent files`)
	}

	const init = () => {
		ready ??= serial(migrate).catch(e => logger.warn('legacy recent files not migrated', e))
		return ready
	}

	/** Metadata in the background; the file opens without waiting for it. `source` is a File or a function resolving to one. */
	const fill = (id, source) => {
		probes = probes
			.then(async () => {
				const file = typeof source === 'function' ? await source() : source
				const meta = await probe(file)
				await serial(async () => {
					const entry = (await storage.all()).find(e => e.id === id)
					if (!entry) return
					await storage.put({ ...entry, ...meta, size: file.size })
				})
				notify()
			})
			.catch(e => logger.warn('probe failed', e))
	}

	/** Stamps an entry used and makes it current, re-read here so a mutation queued before it is never written over; null when it is gone. Run inside `serial`. */
	const use = async id => {
		const entry = (await storage.all()).find(e => e.id === id)
		if (!entry) return null
		const e = { ...entry, usedAt: stamp() }
		await storage.put(e)
		await storage.setMeta('current', e.id)
		return e
	}

	const get = async id => {
		await init()
		return (await storage.all()).find(e => e.id === id) ?? null
	}

	const dropBytes = async entry => {
		if (entry?.storage !== 'opfs') return
		try {
			await files.remove(entry.ref)
		} catch (e) {
			if (e?.name !== 'NotFoundError') logger.warn('import not freed', entry.id, e)
		}
	}

	/** Deletes entries (an import's bytes too), clearing `current` if it was one. Run inside `serial`. */
	const deleteEntries = async entries => {
		const current = await storage.getMeta('current')
		for (const e of entries) {
			await dropBytes(e)
			await storage.delete(e.id)
			if (current === e.id) await storage.setMeta('current', null)
		}
	}

	const remove = async id => {
		await init()
		await serial(async () => {
			const entry = (await storage.all()).find(e => e.id === id)
			await deleteEntries(entry ? [entry] : [])
		})
		notify()
	}

	/** Syncs a folder's entries with its contents: new videos added, vanished ones dropped, a Recent entry for a file inside it adopted, a hidden path skipped. Run inside `serial`; returns what still needs a probe and whether the entries changed. */
	const scan = async folder => {
		const all = await storage.all()
		const mine = all.filter(e => e.storage === 'folder' && e.ref.folder === folder.id)
		// No other folder owns a file under this one (`linkFolder` merges nested folders), so only Recent files and imports can match.
		const outside = all.filter(e => e.storage !== 'folder')
		const hidden = new Set(folder.hidden)
		const stillHidden = []
		const seen = new Set()
		const pending = []
		let changed = false
		for await (const { path, handle } of walk(folder.handle)) {
			const key = path.join('/')
			if (hidden.has(key)) {
				stillHidden.push(key)
				continue
			}
			let entry = mine.find(e => samePath(e.ref.path, path))
			if (!entry) {
				const found = await findByHandle(outside, handle)
				if (found && found.storage !== 'handle') continue
				changed = true
				const ref = { folder: folder.id, path }
				entry = found
					? { ...found, storage: 'folder', ref, origin: handle }
					: newEntry({
							id: await folderEntryId(folder.id, path),
							name: path.at(-1),
							storage: 'folder',
							ref,
							origin: handle,
						})
				await storage.put(entry)
			}
			seen.add(entry.id)
			if (entry.duration == null) pending.push([entry.id, handle])
		}
		const gone = mine.filter(e => !seen.has(e.id))
		await deleteEntries(gone)
		// A hidden file that left the folder is forgotten, so one coming back under its name shows.
		if (stillHidden.length !== hidden.size) await updateFolder(folder.id, { hidden: stillHidden })
		return { pending, changed: changed || gone.length > 0 }
	}

	const probeAll = pending => {
		for (const [id, handle] of pending) fill(id, () => handle.getFile())
	}

	/** The entry's File, or 'needsGesture' | 'denied'. Throws NotFoundError when the file is gone. */
	const fileOf = async (entry, request) => {
		if (entry.storage === 'opfs') return files.read(entry.ref)
		if (entry.storage === 'folder') {
			const folder = (await folderList()).find(f => f.id === entry.ref.folder)
			if (!folder) throw Object.assign(new Error('folder unlinked'), { name: 'NotFoundError' })
			const perm = await permission(folder.handle, request)
			if (perm !== 'granted') return perm
			let dir = folder.handle
			for (const name of entry.ref.path.slice(0, -1)) dir = await dir.getDirectoryHandle(name)
			return (await dir.getFileHandle(entry.ref.path.at(-1))).getFile()
		}
		const perm = await permission(entry.ref, request)
		return perm === 'granted' ? entry.ref.getFile() : perm
	}

	return {
		/** Every entry, most recently used first. */
		async list() {
			await init()
			return (await storage.all()).sort((a, b) => b.usedAt - a.usedAt)
		},

		get,

		/** Records a picked file and makes it current. Without a handle (no File System Access) nothing persists; returns null. */
		async add({ handle, file }) {
			await init()
			if (!handle) return null
			const entry = await serial(async () => {
				const e = await upsert(handle, { name: file.name, size: file.size })
				await storage.setMeta('current', e.id)
				return e
			})
			if (entry.duration == null) fill(entry.id, file)
			notify()
			return entry
		},

		/**
		 * The entry's file. `request: false` (no gesture, e.g. a reload) only queries the permission; `touch: false` (a preload) leaves `usedAt` and `current` alone.
		 * @returns {Promise<{entry, file}|{entry, needsGesture: true}|null>} null = gone (entry removed) or refused
		 */
		async open(id, { request = true, touch = true } = {}) {
			const entry = await get(id)
			if (!entry?.ref) return null
			try {
				const file = await fileOf(entry, request)
				if (file === 'needsGesture') return { entry, needsGesture: true }
				if (file === 'denied') return null
				const opened = (touch && (await serial(() => use(id)))) || entry
				if (opened.duration == null || opened.size !== file.size) fill(id, file)
				if (touch) notify()
				return { entry: opened, file }
			} catch (e) {
				if (e?.name === 'NotFoundError') {
					await remove(id)
					return null
				}
				logger.warn('open failed', e)
				return null
			}
		},

		/** Marks an entry used — a file the stage already holds open went on stage again. */
		async touch(id) {
			await init()
			const done = await serial(() => use(id))
			if (done) notify()
		},

		/** Copies a file into the origin-private file system; the entry then opens after a reload with no prompt. A file already in the library (same handle) becomes the import, keeping its id. Throws when the copy fails (quota). */
		async importFile({ file, handle = null }) {
			await init()
			const entry = await serial(async () => {
				const found = handle ? await findByHandle(await storage.all(), handle) : null
				if (found?.storage === 'opfs') return found
				const base = found ?? newEntry({ name: file.name, usedAt: stamp() })
				try {
					await files.write(base.id, file)
				} catch (e) {
					await files.remove(base.id).catch(() => {})
					throw e
				}
				const imported = {
					...base,
					size: file.size,
					storage: 'opfs',
					ref: base.id,
					origin: handle ?? (found && fileHandleOf(found)),
				}
				await storage.put(imported)
				return imported
			})
			if (!persistAsked) {
				persistAsked = true
				files.persist().catch(e => logger.warn('storage.persist failed', e))
			}
			if (entry.duration == null) fill(entry.id, file)
			notify()
			return entry
		},

		/** Imports a Recent or folder entry in place, asking for its permission if needed. @returns {Promise<object|{needsGesture: true}|null>} the import; null = gone or refused */
		async importEntry(id) {
			const opened = await this.open(id, { touch: false })
			if (!opened?.file) return opened
			return this.importFile({ file: opened.file, handle: fileHandleOf(opened.entry) })
		},

		remove,

		/**
		 * Takes entries out of the library at the user's word. A folder file is hidden, so its folder's rescan skips it until `showHidden`.
		 * @returns {Promise<string[]>} ids removed
		 */
		async discard(ids) {
			await init()
			const removed = await serial(async () => {
				const targets = (await storage.all()).filter(e => ids.includes(e.id))
				for (const f of await folderList()) {
					const paths = targets
						.filter(e => e.storage === 'folder' && e.ref.folder === f.id)
						.map(e => e.ref.path.join('/'))
					if (paths.length) await updateFolder(f.id, { hidden: [...(f.hidden ?? []), ...paths] })
				}
				await deleteEntries(targets)
				return targets.map(e => e.id)
			})
			notify()
			return removed
		},

		/** Brings a folder's hidden files back, asking for its permission if needed. */
		async showHidden(id) {
			await init()
			await serial(() => updateFolder(id, { hidden: [] }))
			return this.rescanFolder(id)
		},

		/** Links a picked directory, or rescans the linked folder that already holds it; a linked folder inside it is absorbed. */
		async linkFolder(handle) {
			await init()
			const { folder, pending } = await serial(async () => {
				const folders = await folderList()
				for (const f of folders) {
					if ((await f.handle.isSameEntry(handle).catch(() => false)) || (await pathWithin(f.handle, handle)))
						return { folder: f, pending: (await scan(f)).pending }
				}
				const retired = (await storage.getMeta('retired')) ?? []
				let back = null
				for (const r of retired) if (await r.handle.isSameEntry(handle).catch(() => false)) back = r
				const created = { id: back?.id ?? crypto.randomUUID(), name: handle.name, handle }
				const kept = []
				const all = await storage.all()
				for (const f of folders) {
					const prefix = await pathWithin(handle, f.handle)
					if (!prefix) {
						kept.push(f)
						continue
					}
					for (const e of all.filter(e => e.storage === 'folder' && e.ref.folder === f.id))
						await storage.put({ ...e, ref: { folder: created.id, path: [...prefix, ...e.ref.path] } })
					created.hidden = [...(created.hidden ?? []), ...(f.hidden ?? []).map(p => [...prefix, p].join('/'))]
				}
				await storage.setMeta('folders', [...kept, created])
				if (back)
					await storage.setMeta(
						'retired',
						retired.filter(r => r !== back)
					)
				return { folder: created, pending: (await scan(created)).pending }
			})
			probeAll(pending)
			notify()
			return folder
		},

		/** Re-reads a linked folder. `request: false` (no gesture) only queries the permission. @returns {Promise<object|{needsGesture: true}|null>} */
		async rescanFolder(id, { request = true } = {}) {
			await init()
			const folder = (await folderList()).find(f => f.id === id)
			if (!folder) return null
			const perm = await permission(folder.handle, request)
			if (perm === 'needsGesture') return { needsGesture: true }
			if (perm !== 'granted') return null
			const scanned = await serial(async () => {
				const now = (await folderList()).find(f => f.id === id)
				return now && scan(now)
			})
			if (!scanned) return null
			probeAll(scanned.pending)
			// A rescan from a click may have granted access, which the browser shows even when no file changed.
			if (scanned.changed || request) notify()
			return folder
		},

		/** Rescans every linked folder whose permission is granted now; one that would prompt is skipped. */
		async rescanAll() {
			for (const f of await this.folders()) await this.rescanFolder(f.id, { request: false })
		},

		/**
		 * Unlinks a folder. A video opened from it stays as a Recent file; the rest leave the library. The folder's id is kept, for a later link of the same directory.
		 * @returns {Promise<string[]>} ids of the entries removed
		 */
		async unlinkFolder(id) {
			await init()
			const removed = await serial(async () => {
				const folders = await folderList()
				const folder = folders.find(f => f.id === id)
				if (!folder) return []
				const mine = (await storage.all()).filter(e => e.storage === 'folder' && e.ref.folder === id)
				const used = mine.filter(e => e.usedAt > 0 && e.origin)
				for (const e of used) await storage.put({ ...e, storage: 'handle', ref: e.origin, origin: null })
				const gone = mine.filter(e => !used.includes(e))
				await deleteEntries(gone)
				const evicted = await evict()
				await storage.setMeta(
					'folders',
					folders.filter(f => f !== folder)
				)
				const retired = (await storage.getMeta('retired')) ?? []
				await storage.setMeta('retired', [{ id, handle: folder.handle }, ...retired].slice(0, RETIRED_MAX))
				return [...gone.map(e => e.id), ...evicted]
			})
			notify()
			return removed
		},

		/** Linked folders, each with `granted`: its videos open now without a prompt. */
		async folders() {
			await init()
			return Promise.all(
				(await folderList()).map(async f => ({
					...f,
					granted: (await permission(f.handle, false).catch(() => 'denied')) === 'granted',
				}))
			)
		},

		/** Ids whose file opens now without a gesture: every import, and handle/folder entries whose permission is granted. */
		async openableIds() {
			await init()
			const granted = new Map()
			const check = async handle => {
				if (!granted.has(handle))
					granted.set(handle, (await permission(handle, false).catch(() => 'denied')) === 'granted')
				return granted.get(handle)
			}
			const folders = new Map((await folderList()).map(f => [f.id, f.handle]))
			const ids = new Set()
			for (const e of await storage.all()) {
				const handle = e.storage === 'folder' ? folders.get(e.ref?.folder) : e.ref
				if (e.storage === 'opfs' || (handle && (await check(handle)))) ids.add(e.id)
			}
			return ids
		},

		/** Asks, inside a click, for every linked folder and Recent file that would prompt, stopping once the gesture is spent. Chrome's restore prompt lists every handle the origin held before, so the first ask usually re-grants them all and may offer "Allow on every visit". */
		async requestAccess() {
			await init()
			const handles = [
				...(await folderList()).map(f => f.handle),
				...(await storage.all()).filter(e => e.storage === 'handle' && e.ref).map(e => e.ref),
			]
			for (const handle of handles) {
				if ((await permission(handle, true).catch(() => 'denied')) === 'needsGesture') break
			}
			notify()
		},

		/** Bytes held by imports, the origin's usage/quota, and whether the browser may evict it. */
		async usage() {
			await init()
			const imported = (await storage.all())
				.filter(e => e.storage === 'opfs')
				.reduce((n, e) => n + (e.size ?? 0), 0)
			const [estimate, persisted] = await Promise.all([
				files.estimate().catch(() => ({})),
				files.persisted().catch(() => false),
			])
			return { imported, usage: estimate.usage ?? null, quota: estimate.quota ?? null, persisted }
		},

		/** The id the stage last loaded, for reopening after a reload. */
		async current() {
			await init()
			return (await storage.getMeta('current')) ?? null
		},

		async clearCurrent() {
			await init()
			await serial(() => storage.setMeta('current', null))
		},

		subscribe(fn) {
			listeners.add(fn)
			return () => listeners.delete(fn)
		},
	}
}

const library = createLibrary(idbLibraryStorage)

if (IS_DEV) window.library = library

export default library
